import React from "react";

import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";

import {
	Alert,
	ButtonStandard,
	FormDatePicker,
	FormInputText,
	FormRadioButtons,
	FormSubscribe,
	ModalBase,
	PYForm,
	PageBanner,
	Spinner,
	SubmitButton,
	Text,
	toast,
} from "@performyard/py-components";
import type { GetReviewCycleDetailsResponseFrontend } from "@performyard/service-contracts";
import { DEFAULT_REVIEW_START_TIME, DEFAULT_TIMEZONE } from "@performyard/utils-isomorphic";

import { useReviewCycleDetails, useUpdateReviewCycle } from "../../../../api/hooks";
import { useCompanySettings } from "../../../../api/hooks/defs/company";

import { LockReviewsField } from "../../Create/ReviewCycleCreate/steps/CycleSettingsStep/LockReviewsField";
import {
	AUTOMATION_TYPE_OPTIONS,
	AutomationType,
} from "../../Create/ReviewCycleCreate/steps/CycleSettingsStep/constants";
import { buildUpdateCyclePayload } from "./buildUpdateCyclePayload";
import { type CycleType, type EditCycleFormData, getEditCycleDefaults, getEditCycleSchema } from "./schema";

type EditCycleDetailsModalProps = {
	reviewCycleId: string;
	isOpen: boolean;
	onClose: () => void;
	onSaved?: () => void;
};

export const EditCycleDetailsModal = ({ reviewCycleId, isOpen, onClose, onSaved }: EditCycleDetailsModalProps) => {
	const { data: details } = useReviewCycleDetails(reviewCycleId);

	const { data: companySettings } = useCompanySettings();
	const companyTimezone = companySettings?.default_timezone ?? DEFAULT_TIMEZONE;
	const reviewSettings = companySettings?.functions?.reviews as { default_review_start_time?: number } | undefined;
	const defaultStartTime = reviewSettings?.default_review_start_time ?? DEFAULT_REVIEW_START_TIME;

	return (
		<ModalBase isOpen={isOpen} onClose={onClose} dangerouslySetClassName="w-[432px]">
			<div className="px-6 pb-4 text-center">
				<Text size="sub-heading" weight="semi-bold">
					Edit Cycle Details
				</Text>
			</div>

			{details ? (
				<EditCycleDetailsForm
					reviewCycleId={reviewCycleId}
					details={details}
					companyTimezone={companyTimezone}
					defaultStartTime={defaultStartTime}
					onClose={onClose}
					onSaved={onSaved}
				/>
			) : (
				<div className="flex justify-center px-6 pb-6">
					<Spinner />
				</div>
			)}
		</ModalBase>
	);
};

type EditCycleDetailsFormProps = {
	reviewCycleId: string;
	details: GetReviewCycleDetailsResponseFrontend;
	companyTimezone: string;
	defaultStartTime: number;
	onClose: () => void;
	onSaved?: () => void;
};

const EditCycleDetailsForm = ({
	reviewCycleId,
	details,
	companyTimezone,
	defaultStartTime,
	onClose,
	onSaved,
}: EditCycleDetailsFormProps) => {
	const cycleType = details.type as CycleType;
	const startDatePassed = details.startDate <= new Date();

	const schema = React.useMemo(() => getEditCycleSchema(cycleType, companyTimezone), [cycleType, companyTimezone]);
	const defaultValues = React.useMemo(
		() => getEditCycleDefaults(details, companyTimezone),
		[details, companyTimezone]
	);

	const { mutateAsync, isPending } = useUpdateReviewCycle({
		onSuccess: () => {
			toast.success("Your changes have been saved");
			onSaved?.();
			onClose();
		},
		onError: () => {
			toast.error("There was an error updating the review cycle.");
		},
	});

	const handleSubmit = async ({ value }: { value: EditCycleFormData }): Promise<void> => {
		const data = buildUpdateCyclePayload({
			cycleType,
			formData: value,
			original: defaultValues,
			companyTimezone,
		});

		// Nothing changed — close without hitting the API (the backend rejects an empty update).
		if (Object.keys(data).length === 0) {
			onClose();
			return;
		}

		try {
			await mutateAsync({ reviewCycleId, data });
		} catch {
			// Surfaced to the user via the mutation's onError toast.
		}
	};

	const startDateWarning =
		cycleType === "automated"
			? "Start date & automation type cannot be edited after the start date has passed."
			: "Start date cannot be edited after it has passed.";

	return (
		<PYForm id="edit-cycle-details" schema={schema} defaultValues={defaultValues} onSubmit={handleSubmit}>
			<div className="flex flex-col gap-4 px-6 pb-2">
				<FormInputText id="name" label="Review Cycle Name" placeholder="Review cycle name" clearable />

				<LockReviewsField goingForwardPretext={true} />

				{cycleType === "automated" && (
					<FormRadioButtons<AutomationType>
						id="automationType"
						label="Select an Automation Type"
						options={AUTOMATION_TYPE_OPTIONS}
						disabled={startDatePassed}
					/>
				)}

				{cycleType === "group" && (
					<div className="flex flex-col gap-1">
						<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
							Group
						</Text>
						<Text size="paragraph-3" weight="regular" color="neutral-7">
							{details.groupName ?? "—"}
						</Text>
					</div>
				)}

				<div className="flex flex-col gap-2">
					{/** Reduced gap of 8px between start date and timezone alert */}
					<FormDatePicker
						id="startDate"
						label="Review Cycle Start Date"
						showTimeDropdown
						showMonthAndYearDropdowns
						defaultTime={defaultStartTime}
						disabled={startDatePassed}
					/>

					<Alert type="neutral" message={`Review cycle timezone is set to ${companyTimezone}`} />
				</div>

				{cycleType === "automated" && (
					<FormSubscribe
						id="automationType-subscribe"
						selector={(state) => state.values?.automationType as AutomationType | undefined}
					>
						{(automationType) =>
							automationType === "recurring" ? (
								<FormDatePicker
									id="automationEndDate"
									label="Automation End Date"
									showMonthAndYearDropdowns
								/>
							) : null
						}
					</FormSubscribe>
				)}

				{startDatePassed && (
					<PageBanner
						color="primary"
						icon={faTriangleExclamation}
						mainTitle={
							<Text weight="semi-bold" size="paragraph-3">
								{startDateWarning}
							</Text>
						}
					/>
				)}
			</div>

			<div className="flex justify-center gap-2 px-6 pt-4 pb-6">
				<div className="w-fit">
					<ButtonStandard variant="outline" color="neutral" size="large" onClick={onClose}>
						Cancel
					</ButtonStandard>
				</div>
				<div className="w-fit min-w-[120px]">
					<SubmitButton color="primary" size="large" loading={isPending}>
						Save Changes
					</SubmitButton>
				</div>
			</div>
		</PYForm>
	);
};

export default EditCycleDetailsModal;
